import { get } from 'lodash-es'

import { debug } from 'src/services/DebugService'
import type { SlateTextOp } from 'src/models/crdtV2'

import { Transcript, TranscriptParagraph, TranscriptSpeaker } from './Transcript'
import { TranscriptEvent, TranscriptEventTag } from './TranscriptEvent'
import {
    TranscriptAddSpeakerOp,
    TranscriptEditSpeakerOp,
    TranscriptMergeParagraphOp,
    TranscriptOp,
    TranscriptSetParagraphOp,
    TranscriptSplitParagraphOp,
} from './TranscriptOperation'
import { DummyTranscriptUserStateManager, TranscriptUserStateManager } from './TranscriptUserState'
import {
    TranscriptConnectionState,
    TranscriptConnectionStateManager,
    DEFAULT_TRANSCRIPT_CONNECTION_STATE,
} from './TranscriptConnectionState'

export type TranscriptSubscriptionCallback = (events: TranscriptEvent[]) => void
export type TranscriptSubscriptionsMap = { [path: string]: TranscriptSubscriptionCallback[] }

export type TranscriptApplyOrigin = 'speaker_box' | 'transcript_editor'
export type TranscriptSource = 'dummy' | 'ably' | 'rt-broker' | 'document'

export type TranscriptApplyOptions = {
    /**
     * Origin of the change. (e.g. 'speaker_box' or 'transcript_editor')
     */
    origin: TranscriptApplyOrigin

    /**
     * Tags provide additional metadata for the change.
     */
    tags?: TranscriptEventTag[]
}

/**
 * TranscriptClient is an interface for a data source (such as ably or yjs) to implement. It provides a standardized way
 * to query and mutate the transcript as well as subscribe to changes.
 */
export type TranscriptClient = {
    readonly source: TranscriptSource
    /**
     * When true, mutation methods (`applyOps`, the convenience wrappers, `undo`/`redo`)
     * are not allowed to change the transcript. This is a defence-in-depth guard
     * alongside any UI-level edit gating: a UI bug shouldn't be able to write
     * to a doc that the user lacks edit permission on.
     *
     * Phase 1 keeps this `true` everywhere (viewer-only). `setReadOnly` updates the flag.
     * A client with no write path overrides `setReadOnly` and leaves the flag unchanged.
     */
    readonly isReadOnly: boolean
    setReadOnly: (isReadOnly: boolean) => void
    init: () => Promise<void>
    destroy: () => void
    subscribe: (path: string, cb: TranscriptSubscriptionCallback) => () => void
    unsubscribe: (path: string, cb: TranscriptSubscriptionCallback) => void

    getUserState: () => TranscriptUserStateManager
    getConnectionState: () => TranscriptConnectionStateManager

    // queries
    getTotalParagraphCount: () => number
    isEmpty: () => boolean
    getParagraphIds: () => string[]
    getParagraphById: (id: string) => TranscriptParagraph
    getParagraphByIndex: (index: number) => TranscriptParagraph
    getParagraphIndexByTime: (timestamp: number) => number
    getParagraphIndexByInsightOffset: (timestamp: number) => number
    getRecordingStartedAt: () => number

    getSpeakers: () => TranscriptSpeaker[]
    getSpeakerById: (id: string) => TranscriptSpeaker
    isDiarizationSupported: () => boolean

    /**
     * Returns a plain js object for the given path. If no path is passed, it will return the whole transcript.
     *
     * Note: `toJS()` is required to return the same reference to an object every time unless the data changed from last time it was called.
     * This is essential for react to avoid unnecessary re-renders and infinite loops.
     */
    toJS: <T>(path?: string) => T | undefined

    // mutations
    /**
     * Applies a list of operations to the transcript.
     * IMPORTANT: This method has to be overridden by any class implementing this interface.
     */
    applyOps: (ops: TranscriptOp[], options: TranscriptApplyOptions) => void
    undo: () => void
    redo: () => void

    addSpeaker: (op: Omit<TranscriptAddSpeakerOp, 'type'>, options: TranscriptApplyOptions) => void
    editSpeaker: (op: Omit<TranscriptEditSpeakerOp, 'type'>, options: TranscriptApplyOptions) => void
    setParagraph: (op: Omit<TranscriptSetParagraphOp, 'type'>, options: TranscriptApplyOptions) => void
    splitParagraph: (op: Omit<TranscriptSplitParagraphOp, 'type'>, options: TranscriptApplyOptions) => void
    mergeParagraph: (op: Omit<TranscriptMergeParagraphOp, 'type'>, options: TranscriptApplyOptions) => void
    /**
     * Writes one finalized-text edit by sending a `paragraph_text` op through `applyOps`.
     */
    applyParagraphText: (paragraphId: string, op: SlateTextOp) => void
}

/**
 * Base class for transcript clients.
 *
 * It's a simple implementation of the TranscriptClient interface.
 * It's not meant to be used directly, but rather to be extended by concrete implementations.
 *
 * It's already handles generic functionality like subscriptions and emitting events.
 */
export abstract class BaseTranscriptClient implements TranscriptClient {
    abstract readonly source: TranscriptSource
    private _isReadOnly = true

    get isReadOnly(): boolean {
        return this._isReadOnly
    }
    /**
     * Methods to be overridden by concrete implementations.
     */
    abstract init: () => Promise<void>
    abstract destroy: () => void
    abstract applyOps: (ops: TranscriptOp[], options: TranscriptApplyOptions) => void
    abstract undo: () => void
    abstract redo: () => void
    abstract getUserState: () => TranscriptUserStateManager
    abstract getConnectionState: () => TranscriptConnectionStateManager

    private _subscriptions: TranscriptSubscriptionsMap = {}

    /**
     * Represents the latest version of the transcript.
     * This member is used by all the query methods implemented in this base class.
     *
     * IMPORTANT: This member has to be updated by all child classes extending this base class to always contain the latest version of the transcript.
     */
    protected _transcript: Transcript = Transcript.EMPTY

    /**
     * Emits a list of events to the subscribers.
     *
     * It's important to emit a list of events instead of a single event because some events can be emitted in a chain.
     * For example, when a paragraph is split, the parent paragraph is deleted and a new paragraph is created.
     * Also it avoids higher-up subscriptions from being called multiple times.
     */
    protected emitEvents(events: TranscriptEvent[]) {
        const invocationMap: { [path: string]: TranscriptEvent[] } = {}

        for (const event of events) {
            // iterate every level of the path. If the path is `paragraphs.123.content`, `paragraphs.123` and `paragraphs` will be emitted too
            for (let i = 0; i <= event.path.length; i++) {
                const path = event.path.slice(0, i).join('.')

                if (!invocationMap[path]) {
                    invocationMap[path] = []
                }
                invocationMap[path].push(event)
            }

            /**
             * if we receive an event that the root map changed, we also want to emit events for each key that changed
             * this is a compatibility workaround to make sure BE updates are always displayed correctly
             *
             * NOTE: We're sending the root map event to subscribers of each key that changed. This might cause problems inside those subscribers
             * if not handled correctly.
             */
            if (event.path.length === 0 && event.type === 'map') {
                for (const key of event.delta[0]?.change ?? []) {
                    if (!invocationMap[key]) {
                        invocationMap[key] = []
                    }
                    invocationMap[key].push(event)
                }
            }
        }

        for (const path in invocationMap) {
            for (const cb of this._subscriptions[path] ?? []) {
                cb(invocationMap[path])
            }
        }
    }

    /**
     * Subscribes to all events that happen within given path including children paths.
     *
     * Example: if you subscribe to `paragraphs.123` you will also receive events for `paragraphs.123.content`, `paragraphs.123.content.0`, etc.
     */
    subscribe(path: string, cb: TranscriptSubscriptionCallback): () => void {
        if (!this._subscriptions[path]) {
            this._subscriptions[path] = []
        }
        this._subscriptions[path].push(cb)

        return () => this.unsubscribe(path, cb)
    }

    /**
     * Unsubscribes from all events that happen within given path including children paths.
     */
    unsubscribe(path: string, cb: TranscriptSubscriptionCallback): void {
        if (!this._subscriptions[path]) {
            return
        }
        this._subscriptions[path] = this._subscriptions[path].filter((fn) => fn !== cb)
    }

    getRecordingStartedAt = () => this._transcript.recordingStartedAt
    getTotalParagraphCount = () => this._transcript.paragraphIds.length
    isEmpty = () => this.getTotalParagraphCount() === 0
    getParagraphIds = () => this._transcript.paragraphIds
    getParagraphById = (id: string) => Transcript.getParagraphById(this._transcript, id)
    getParagraphByIndex = (index: number) => this.getParagraphById(this.getParagraphIds()[index])
    getParagraphIndexByTime = (timestamp: number) => Transcript.getParagraphIndexByTime(this._transcript, timestamp)
    getParagraphIndexByInsightOffset = (timestamp: number) => Transcript.getParagraphIndexByInsightOffset(this._transcript, timestamp)
    getSpeakers = () => Transcript.getSpeakers(this._transcript)
    getSpeakerById = (id: string) => Transcript.getSpeakerById(this._transcript, id)
    isDiarizationSupported = () => Transcript.isDiarizationSupported(this._transcript)
    toJS = <T>(path?: string) => {
        if (path) {
            return get(this._transcript, path)
        }

        return this._transcript as T
    }

    addSpeaker(op: Omit<TranscriptAddSpeakerOp, 'type'>, options: TranscriptApplyOptions) {
        this.applyOps([{ type: 'add_speaker', ...op }], options)
    }
    editSpeaker(op: Omit<TranscriptEditSpeakerOp, 'type'>, options: TranscriptApplyOptions) {
        this.applyOps([{ type: 'edit_speaker', ...op }], options)
    }
    setParagraph(op: Omit<TranscriptSetParagraphOp, 'type'>, options: TranscriptApplyOptions) {
        this.applyOps([{ type: 'set_paragraph', ...op }], options)
    }
    splitParagraph(op: Omit<TranscriptSplitParagraphOp, 'type'>, options: TranscriptApplyOptions) {
        this.applyOps([{ type: 'split_paragraph', ...op }], options)
    }
    mergeParagraph(op: Omit<TranscriptMergeParagraphOp, 'type'>, options: TranscriptApplyOptions) {
        this.applyOps([{ type: 'merge_paragraph', ...op }], options)
    }

    setReadOnly = (isReadOnly: boolean) => {
        this._isReadOnly = isReadOnly
    }

    applyParagraphText = (paragraphId: string, op: SlateTextOp) => {
        this.applyOps([{ type: 'paragraph_text', paragraphId, edit: op }], { origin: 'transcript_editor' })
    }
}

type DummyTranscriptClientOptions = {
    transcript?: Transcript
    initialConnectionState?: TranscriptConnectionState
}

export class DummyTranscriptClient extends BaseTranscriptClient {
    readonly source = 'dummy' as const
    private _userStateManager = new DummyTranscriptUserStateManager()
    private _connectionStateManager: TranscriptConnectionStateManager

    constructor(options: DummyTranscriptClientOptions = {}) {
        super()
        this._transcript = options.transcript ?? Transcript.EMPTY
        this._connectionStateManager = new TranscriptConnectionStateManager(
            options.initialConnectionState ?? DEFAULT_TRANSCRIPT_CONNECTION_STATE,
        )
    }
    init = async () => {}
    destroy = () => {}

    getUserState = () => this._userStateManager
    getConnectionState = () => this._connectionStateManager

    applyOps = (ops: TranscriptOp[], options?: TranscriptApplyOptions) => {
        debug.log('DummyTranscriptClient.applyOps()', ops, options)
    }

    undo = () => {}
    redo = () => {}

    processAsrHistory = () => {}
    processAsrQueue = () => {}
}
